import { t } from '../../../shared/config/translations'
import type { FilterValue } from '../../../shared/filters/collectionFilterQuery'
import {
	COLLECTION_SORT_OPTIONS,
	getCollectionSortSpec,
	MEDIA_FILTER_OPTIONS,
	RATING_FILTER_OPTIONS,
	STATUS_FILTER_OPTIONS,
} from '../../../shared/filters/collectionFilters'
import type {
	CollectionSort,
	CollectionSortFilterOptions,
	MediaFilterOptions,
	RatingFilterOptions,
	StatusFilterOptions,
} from '../../../shared/types/collection'
import type { DiscogsFolder } from '../../../shared/types/discogs'
import { renderMultiSelect } from '../inputs/multiSelectTemplates'
import { renderSelect } from '../inputs/selectTemplates'
import { renderSwitch } from '../inputs/switchTemplates'
import {
	type FiltersOptions,
	type FiltersState,
	hasActiveFilters,
} from './filterState'

type YearRange = { min: number | null; max: number | null } | undefined

/** Renders the collection sort select with the provided option list. */
export function renderSortFilter(
	currentSort: CollectionSortFilterOptions,
	options: Array<{ value: CollectionSort; label: string }>,
): string {
	const { sort, sortOrder } = getCollectionSortSpec(currentSort)
	const select = renderSelect({
		label: t('collection.sortBy'),
		options: options
			.map(
				({ value, label }) =>
					`<option value="${value}" ${sort === value ? 'selected' : ''}>${label}</option>`,
			)
			.join('\n'),
		selectId: 'col-sort-by-filter',
	})

	return `
		${select}
		<button
			type="button"
			id="col-sort-direction"
			class="btn outlined icon filter-icon-button sort-direction-button"
			aria-label="${t('collection.sortReverseDirection')}"
			aria-pressed="${sortOrder === 'desc'}"
			title="${t('collection.sortReverseDirection')}"
		>
			<svg viewBox="0 0 24 24" aria-hidden="true">
				<path d="M12 19V5"></path>
				<path d="m5 12 7-7 7 7"></path>
			</svg>
		</button>
	`
}

/** Renders the folder select once Discogs folder options are available. */
export function renderFolderFilterWithOptions(
	folders: DiscogsFolder[],
	selected: FilterValue<number> | null = null,
): string {
	return renderMultiSelect({
		searchable: true,
		allLabel: t('collection.folderAll'),
		allSelected: selected === null,
		label: t('collection.folder'),
		selectId: 'col-folder-filter',
		selected: toStringArray(selected),
		options: folders.map((folder) => ({
			value: String(folder.id),
			label: folder.name,
		})),
	})
}

/** Renders the physical media filter select. */
export function renderMediaFilter(
	selected: FilterValue<MediaFilterOptions> = 'all',
): string {
	return renderMultiSelect({
		allValue: 'all',
		allLabel: t('collection.mediaAll'),
		allSelected: selected === 'all',
		label: t('collection.media'),
		options: getMediaFilterOptions().filter(({ value }) => value !== 'all'),
		selectId: 'col-media-filter',
		selected: toStringArray(selected).filter((value) => value !== 'all'),
	})
}

/** Renders the personal Discogs rating filter. */
export function renderRatingFilter(
	selected: FilterValue<RatingFilterOptions> | undefined = undefined,
): string {
	return renderMultiSelect({
		allLabel: t('collection.ratingAny'),
		allSelected: selected === undefined,
		label: t('collection.ratingFilter'),
		options: RATING_FILTER_OPTIONS.map((value) => ({
			value: String(value),
			label:
				value === 0
					? t('collection.ratingUnrated')
					: `${value} ${t(value === 1 ? 'collection.ratingStar' : 'collection.ratingStars')}`,
			displayLabel: value === 0 ? undefined : '★'.repeat(value),
		})),
		selectId: 'col-rating-filter',
		selected: toStringArray(selected),
	})
}

/** Renders a genre select from facet values returned by the server. */
export function renderGenreFilter(
	selected: FilterValue<string> | undefined = undefined,
	options: string[] = [],
): string {
	return renderMultiSelect({
		searchable: true,
		allLabel: t('collection.genreAny'),
		allSelected: selected === undefined,
		label: 'Genre',
		options: [...new Set([...options, ...toStringArray(selected)])].map(
			(value) => ({ value, label: value }),
		),
		selectId: 'col-genre-filter',
		selected: toStringArray(selected),
	})
}

/** Renders a style select from facet values returned by the server. */
export function renderStyleFilter(
	selected: FilterValue<string> | undefined = undefined,
	options: string[] = [],
): string {
	return renderMultiSelect({
		searchable: true,
		allLabel: t('collection.styleAny'),
		allSelected: selected === undefined,
		label: 'Style',
		options: [...new Set([...options, ...toStringArray(selected)])].map(
			(value) => ({ value, label: value }),
		),
		selectId: 'col-style-filter',
		selected: toStringArray(selected),
	})
}

/** Renders the icon button that toggles dimming hidden collection releases. */
export function renderDimReleasesFilter(
	dimReleases: boolean = true,
	variant: 'outlined' | 'noFrame' = 'outlined',
): string {
	const icon = dimReleases ? '#symbols-eye-closed' : '#symbols-eye'
	return `
        <button
            type="button"
            id="col-dim-releases-filter"
            class="btn ${variant} icon filter-icon-button filter-icon-toggle"
            aria-label="${t(dimReleases ? 'collection.undimReleasesFilter' : 'collection.dimReleasesFilter')}"
            aria-pressed="${dimReleases}"
        >
            <svg><use href="${icon}"></use></svg>
        </button>
    `
}

/** Renders the listened/hidden status filter select. */
export function renderStatusFilter(
	selected: FilterValue<StatusFilterOptions> = 'all',
	favoritesOnly = false,
	includeFavoritesOnly = false,
): string {
	const selectedValues = toStringArray(selected).filter(
		(value) => value !== 'all',
	)
	if (includeFavoritesOnly && favoritesOnly) {
		selectedValues.push('favoritesOnly')
	}

	return renderMultiSelect({
		allValue: 'all',
		allLabel: t('collection.historyAll'),
		allSelected: selected === 'all',
		label: t('collection.statusFilter'),
		options: [
			...STATUS_FILTER_OPTIONS.filter(({ value }) => value !== 'all').map(
				({ value, labelKey }) => ({
					value,
					label: t(labelKey),
				}),
			),
			...(includeFavoritesOnly
				? [
						{
							value: 'favoritesOnly',
							label: t('common.favoritesOnly'),
							includeInAll: false,
						},
					]
				: []),
		],
		selectId: 'col-status-filter',
		selected: selectedValues,
	})
}

/** Renders paired year-from and year-to selects constrained by the available year range. */
export function renderYearRangeFilter(
	selectedFrom?: number,
	selectedTo?: number,
	range?: YearRange,
): string {
	const years = getYearOptions(range)
	const renderOptions = (
		selected: number | undefined,
		filterFn: (year: number) => boolean,
	) => {
		const numericYears = years.filter(
			(year): year is number =>
				typeof year === 'number' && filterFn(year),
		)
		const opts = ['-', ...numericYears]
		return opts
			.map((val) => {
				const isSelected =
					(val === '-' && selected === undefined) || selected === val
				const label = val === '-' ? t('shuffle.anyYear') : val
				return `<option value="${val}" ${isSelected ? 'selected' : ''}>${label}</option>`
			})
			.join('\n')
	}

	return `
        <div class="year-range-filters">
			${renderSelect({
				label: `${t('shuffle.yearFrom')[0].toUpperCase()}${t('shuffle.yearFrom').slice(1)}`,
				labelSuffix: '',
				options: renderOptions(selectedFrom, (year) =>
					selectedTo ? year <= selectedTo : true,
				),
				selectId: 'col-year-from-filter',
			})}
			${renderSelect({
				label: t('shuffle.yearUntil'),
				labelSuffix: '',
				options: renderOptions(selectedTo, (year) =>
					selectedFrom ? year >= selectedFrom : true,
				),
				selectId: 'col-year-to-filter',
			})}
        </div>
    `
}

/** Renders updated year range options for one side of the paired year filter. */
export function renderYearRangeOptions(
	selectedFrom: number | undefined,
	selectedTo: number | undefined,
	range: YearRange,
	which: 'from' | 'to',
): string {
	const years = getYearOptions(range)
	const filterFn =
		which === 'from'
			? (year: number) => (selectedTo ? year <= selectedTo : true)
			: (year: number) => (selectedFrom ? year >= selectedFrom : true)

	const numericYears = years.filter(
		(year): year is number => typeof year === 'number' && filterFn(year),
	)
	const opts = ['-', ...numericYears]
	return opts
		.map((val) => {
			const isSelected =
				(val === '-' &&
					(which === 'from'
						? selectedFrom === undefined
						: selectedTo === undefined)) ||
				(which === 'from' ? selectedFrom === val : selectedTo === val)
			const label = val === '-' ? t('shuffle.anyYear') : val
			return `<option value="${val}" ${isSelected ? 'selected' : ''}>${label}</option>`
		})
		.join('\n')
}

/** Returns translated media filter options for select rendering. */
function getMediaFilterOptions(): Array<{
	value: MediaFilterOptions
	label: string
}> {
	return MEDIA_FILTER_OPTIONS.map(({ value, labelKey }) => ({
		value,
		label: t(labelKey),
	}))
}

/** Returns translated collection sort options for select rendering. */
export function getCollectionSortOptions(): Array<{
	value: CollectionSort
	label: string
}> {
	return COLLECTION_SORT_OPTIONS.map(({ value, labelKey }) => ({
		value,
		label: t(labelKey),
	}))
}

/** Builds the descending list of years available to year range selects. */
function getYearOptions(range?: YearRange): Array<number | string> {
	if (!range || range.min == null || range.max == null) return []
	const start = range.max
	const end = range.min
	const years: number[] = []
	for (let year = start; year >= end; year -= 1) {
		years.push(year)
	}
	return years
}

function toStringArray<T extends string | number>(
	value: FilterValue<T> | null | undefined,
): string[] {
	if (value == null) return []
	const values = Array.isArray(value) ? value : [value]
	return values.map(String).filter(Boolean)
}

export function filtersTemplate(
	state: FiltersState,
	options: FiltersOptions,
	controlIdPrefix: string,
	foldersHtml: string,
): string {
	const favoritesOnlyFilter = isVisible(options.showFavoritesOnly)
		? favoritesOnlyFilterTemplate(state, controlIdPrefix)
		: ''
	const hasFavoritesOnlyInStatus = Boolean(
		options.favoritesOnlyInStatus &&
			isVisible(options.showStatus) &&
			isVisible(options.showFavoritesOnly),
	)
	const statusFilter = isVisible(options.showStatus)
		? renderStatusFilter(
				state.status,
				state.favoritesOnly,
				hasFavoritesOnlyInStatus,
			)
		: ''
	const parts = [
		options.showIncludeHidden || hasFavoritesOnlyInStatus
			? ''
			: favoritesOnlyFilter,
		`<div data-folder-slot style="display:contents">${foldersHtml}</div>`,
		isVisible(options.showMedia) ? renderMediaFilter(state.media) : '',
		statusFilter,
		isVisible(options.showRating) ? renderRatingFilter(state.rating) : '',
		isVisible(options.showGenre)
			? renderGenreFilter(state.genre, options.genreOptions ?? [])
			: '',
		isVisible(options.showStyle)
			? renderStyleFilter(state.style, options.styleOptions ?? [])
			: '',
		isVisible(options.showYearRange)
			? renderYearRangeFilter(
					state.yearFrom,
					state.yearTo,
					options.yearRange,
				)
			: '',
		options.showIncludeHidden ? favoritesOnlyFilter : '',
		options.showIncludeHidden
			? includeHiddenFilterTemplate(state, controlIdPrefix)
			: '',
		isVisible(options.showSort) && state.sort
			? renderSortFilter(state.sort, getCollectionSortOptions())
			: '',
		isVisible(options.dimReleases)
			? renderDimReleasesFilter(state.dimReleases)
			: '',
		resetFiltersButtonTemplate(state, options.resetState),
	]

	return parts.filter(Boolean).join('\n')
}

function favoritesOnlyFilterTemplate(
	state: FiltersState,
	controlIdPrefix: string,
): string {
	return `<div class="filter-switch favorites-only-filter">${renderSwitch({
		id: `${controlIdPrefix}-favorites-only`,
		'data-filter-key': 'favoritesOnly',
		label: t('common.favoritesOnly'),
		labelPrefix:
			'<svg class="filter-switch-icon favorite-filter-icon" aria-hidden="true"><use href="#symbols-heart"></use></svg>',
		checked: !!state.favoritesOnly,
	})}</div>`
}

function includeHiddenFilterTemplate(
	state: FiltersState,
	controlIdPrefix: string,
): string {
	const helpTextId = `${controlIdPrefix}-include-hidden-help-text`
	return `<div class="filter-switch shuffle-include-hidden">${renderSwitch({
		id: `${controlIdPrefix}-include-hidden`,
		'data-filter-key': 'includeHidden',
		label: t('shuffle.includeHidden'),
		labelPrefix: `<svg class="filter-switch-icon hidden-filter-icon" aria-hidden="true"><use href="${state.includeHidden ? '#symbols-eye' : '#symbols-eye-closed'}"></use></svg>`,
		labelSuffix: `
				<button
					type="button"
					class="include-hidden-help"
					aria-label="${t('shuffle.includeHiddenHelp')}"
					aria-describedby="${helpTextId}"
				>
					<svg aria-hidden="true"><use href="#symbols-info-circle"></use></svg>
					<span id="${helpTextId}" class="include-hidden-help-text" role="tooltip">${t('shuffle.includeHiddenHelp')}</span>
				</button>
			`,
		checked: !!state.includeHidden,
	})}</div>`
}

function resetFiltersButtonTemplate(
	state: FiltersState,
	resetState: Partial<FiltersState> | undefined,
): string {
	if (!resetState) return ''

	const isVisible = hasActiveFilters(state, resetState)
	return `
			<button
				type="button"
				class="btn outlined small filters-reset${isVisible ? '' : ' is-hidden'}"
				data-filters-reset
				aria-label="${t('common.resetFilters')}"
			>
				<svg aria-hidden="true"><use href="#symbols-reset"></use></svg>
				<span>${t('common.resetFilters')}</span>
			</button>
		`
}

function isVisible(value: boolean | undefined): boolean {
	return value ?? true
}
